import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { AnalyticsSummary, SummaryDimensionRow } from '../../../shared/api/analytics.api'
import { fmtInt, fmtRub, fmtRubShort } from './summaryFormat'

interface Row {
  label: string
  a: number
  b: number
  fmt: (n: number) => string
  /** true — метрика в процентах, дельта в п.п. */
  isPct?: boolean
}

// Дельта = изменение выбранного периода (A) относительно периода сравнения (B):
// (A − B) / B. До 02.09.2026 формула была перевёрнута — рост июля к августу
// показывался зелёным «↑» при реальном падении (вопрос заказчика №1).
function deltaCell(a: number, b: number, isPct?: boolean) {
  if (isPct) {
    const d = Math.round((a - b) * 10) / 10
    const color = d > 0 ? 'text-emerald-600' : d < 0 ? 'text-red-600' : 'text-gray-400'
    return (
      <span className={color}>
        {d > 0 ? '+' : ''}
        {d} п.п.
      </span>
    )
  }
  if (b === 0) {
    return <span className="text-gray-400">{a > 0 ? 'новое' : '—'}</span>
  }
  const d = Math.round(((a - b) / b) * 100)
  const color = d > 0 ? 'text-emerald-600' : d < 0 ? 'text-red-600' : 'text-gray-400'
  return (
    <span className={color}>
      {d > 0 ? '↑' : d < 0 ? '↓' : ''} {Math.abs(d)}%
    </span>
  )
}

function topSegments(a: AnalyticsSummary, b: AnalyticsSummary): SummaryDimensionRow[] {
  const keys = new Set<string>()
  const rank = [...a.segments, ...b.segments]
    .sort((x, y) => y.orderedSum - x.orderedSum)
    .map((r) => r.key)
  const out: string[] = []
  for (const k of rank) {
    if (!keys.has(k)) {
      keys.add(k)
      out.push(k)
    }
    if (out.length >= 8) break
  }
  return out.map((key) => ({ key, volume: 0, orderedCount: 0, orderedSum: 0 }))
}

export function SummaryCompare({
  a,
  b,
  labelA,
  labelB,
}: {
  a: AnalyticsSummary
  b: AnalyticsSummary
  labelA: string
  labelB: string
}) {
  const rows: Row[] = [
    { label: 'Заявок создано', a: a.kpi.requests.current, b: b.kpi.requests.current, fmt: fmtInt },
    {
      label: 'Новых клиентов',
      a: a.kpi.newClients.current,
      b: b.kpi.newClients.current,
      fmt: fmtInt,
    },
    {
      label: 'Вернувшихся клиентов',
      a: a.kpi.returningClients.current,
      b: b.kpi.returningClients.current,
      fmt: fmtInt,
    },
    { label: 'Позиций', a: a.kpi.items.current, b: b.kpi.items.current, fmt: fmtInt },
    {
      label: 'Сумма КП выставлено',
      a: a.kpi.quotedAmount.current,
      b: b.kpi.quotedAmount.current,
      fmt: fmtRub,
    },
    {
      label: 'Сумма в заказ',
      a: a.kpi.orderedAmount.current,
      b: b.kpi.orderedAmount.current,
      fmt: fmtRub,
    },
    { label: 'В заказ (заявок)', a: a.outcomes.won, b: b.outcomes.won, fmt: fmtInt },
    { label: 'Отказ (заявок)', a: a.outcomes.lost, b: b.outcomes.lost, fmt: fmtInt },
    {
      label: 'Конверсия КП→заказ',
      a: a.kpi.conversionRate.current,
      b: b.kpi.conversionRate.current,
      fmt: (n) => `${n}%`,
      isPct: true,
    },
  ]

  const moneyData = [
    { name: 'Сумма КП', a: a.kpi.quotedAmount.current, b: b.kpi.quotedAmount.current },
    { name: 'В заказ', a: a.kpi.orderedAmount.current, b: b.kpi.orderedAmount.current },
  ]

  const segKeys = topSegments(a, b)
  const aByKey = new Map(a.segments.map((r) => [r.key, r.orderedSum]))
  const bByKey = new Map(b.segments.map((r) => [r.key, r.orderedSum]))
  const segData = segKeys.map((s) => ({
    name: s.key,
    a: aByKey.get(s.key) ?? 0,
    b: bByKey.get(s.key) ?? 0,
  }))

  return (
    <div className="space-y-4">
      {/* Таблица сравнения */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
              <th className="py-2 pr-3 font-medium">Показатель</th>
              <th className="py-2 px-3 font-medium text-right">{labelA}</th>
              <th className="py-2 px-3 font-medium text-right">{labelB}</th>
              <th className="py-2 pl-3 font-medium text-right">Δ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-gray-50">
                <td className="py-2 pr-3 text-gray-700">{r.label}</td>
                <td className="py-2 px-3 text-right text-gray-900 font-medium tabular-nums">
                  {r.fmt(r.a)}
                </td>
                <td className="py-2 px-3 text-right text-gray-600 tabular-nums">{r.fmt(r.b)}</td>
                <td className="py-2 pl-3 text-right font-medium tabular-nums">
                  {deltaCell(r.a, r.b, r.isPct)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Парные столбики */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-gray-900">
            Деньги: {labelA} vs {labelB}
          </h3>
          <div className="mt-3 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={moneyData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickFormatter={fmtRubShort}
                  width={70}
                />
                <Tooltip
                  formatter={(v) => fmtRub(Number(v))}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                />
                <Legend />
                <Bar dataKey="a" name={labelA} fill="#94a3b8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="b" name={labelB} fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-gray-900">
            Сегменты в заказ: {labelA} vs {labelB}
          </h3>
          <div className="mt-3 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={segData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  interval={0}
                  angle={-20}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickFormatter={fmtRubShort}
                  width={70}
                />
                <Tooltip
                  formatter={(v) => fmtRub(Number(v))}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                />
                <Legend />
                <Bar dataKey="a" name={labelA} fill="#94a3b8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="b" name={labelB} fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  )
}
